import React, { useState } from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import { useTheme, createTheme, ThemeProvider } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';

// Mascot SVG (friendly meal mascot)
const Mascot = ({ size = 54 }) => (
  <svg width={size} height={size} viewBox="0 0 60 60" fill="none">
    <ellipse cx="30" cy="30" rx="28" ry="28" fill="#0DB7C4" opacity=".14"/>
    <ellipse cx="30" cy="30" rx="21" ry="21" fill="#0DB7C4" opacity=".26"/>
    <ellipse cx="30" cy="37" rx="12" ry="6" fill="#fff" opacity=".85"/>
    <circle cx="30" cy="24" r="13" fill="#fff"/>
    <ellipse cx="25" cy="25" rx="2.5" ry="3.5" fill="#2C2360"/>
    <ellipse cx="35" cy="25" rx="2.5" ry="3.5" fill="#2C2360"/>
    <ellipse cx="30" cy="31" rx="4.5" ry="2.5" fill="#0DB7C4" opacity=".32"/>
    <rect x="20" y="18" width="4" height="2" rx="1" fill="#0DB7C4"/>
    <rect x="36" y="18" width="4" height="2" rx="1" fill="#0DB7C4"/>
  </svg>
);

// 7EDGE "7" Logo SVG
const SevenEdgeLogo = ({ size = 54 }) => (
  <svg width={size} height={size} viewBox="0 0 68 92" style={{ display: 'block' }} xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="0" width="32" height="25" fill="#FFB300"/>
    <rect x="32" y="0" width="30" height="25" fill="#2C2360"/>
    <rect x="32" y="25" width="30" height="61" fill="#0DB7C4"/>
  </svg>
);

// Google logo for button
const GoogleIcon = () => (
  <Box sx={{
    width: 22, height: 22, borderRadius: '50%', background: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5,
    boxShadow: '0 1px 4px rgba(60,64,67,0.08)',
  }}>
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.20455C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.845H13.8436C13.635 11.9705 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9459 17.64 9.20455Z"/>
      <path fill="#34A853" d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4205 9 14.4205C6.65591 14.4205 4.67182 12.8373 3.96409 10.71H0.957272V13.0418C2.43818 15.9832 5.48182 18 9 18Z"/>
      <path fill="#FBBC05" d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29H0.957272C0.347727 8.55 0 10.0964 0 11.3182C0 12.54 0.347727 14.0864 0.957272 15.3464L3.96409 13.0418V10.71Z"/>
      <path fill="#EA4335" d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4395 4.925L15.0218 2.34318C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957272 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z"/>
    </svg>
  </Box>
);

// Light and dark themes
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    background: { default: "#fafdff" },
    text: { primary: "#1a1b26", secondary: "#50586c" },
  },
});
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: "#191944" },
    text: { primary: "#fff", secondary: "#cdd3e6" },
  },
});

// Helper for glassy background squares
const GlassySquare = ({ sx = {}, ...props }) => (
  <Box
    sx={{
      position: "absolute",
      borderRadius: 2,
      opacity: 0.13,
      filter: "blur(1.2px)",
      zIndex: 1,
      ...sx,
    }}
    {...props}
  />
);

const Login = () => {
  const { login } = useAuth();
  const [darkMode, setDarkMode] = useState(false);

  const handleGoogleLogin = () => {
    try {
      const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
      const clientId = process.env.REACT_APP_CLIENT_ID;
      const redirectUri = encodeURIComponent(window.location.origin + '/');
      if (!cognitoDomain || !clientId) {
        console.error('Missing Cognito configuration:', { cognitoDomain, clientId });
        throw new Error('Missing Cognito configuration');
      }
      const responseType = 'code';
      const scope = 'email openid profile aws.cognito.signin.user.admin';
      const identityProvider = 'Google';
      const authUrl = `${cognitoDomain}/oauth2/authorize?` +
        `client_id=${clientId}&` +
        `response_type=${responseType}&` +
        `scope=${scope}&` +
        `redirect_uri=${redirectUri}&` +
        `identity_provider=${identityProvider}`;
      window.location.href = authUrl;
    } catch (error) {
      console.error('Login error:', error);
      alert('Failed to start login process. Please try again.');
    }
  };

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          background: darkMode
            ? 'linear-gradient(90deg, #191944 0%, #221b4a 100%)'
            : 'linear-gradient(90deg, #16152a 0%, #221b4a 100%)',
          overflow: 'hidden',
        }}
      >
        {/* Left Panel */}
        <Box
          sx={{
            flex: { xs: '0 0 100%', sm: '0 0 40%', md: '0 0 38%' },
            minWidth: 0,
            display: { xs: 'none', sm: 'flex' }, // Hide on mobile, show on tablet+
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: 2 },
            background: 'linear-gradient(120deg, #15132a 0%, #21194d 100%)',
            overflow: 'hidden',
            borderRight: '2.5px solid #29235c',
          }}
        >
          {/* Glassy meal-themed squares */}
          <GlassySquare sx={{ top: 40, left: 60, width: 50, height: 50, bgcolor: "#0DB7C4" }}/>
          <GlassySquare sx={{ bottom: 40, left: 30, width: 38, height: 38, bgcolor: "#FFB300", opacity: .10 }}/>
          <GlassySquare sx={{ top: 300, left: 130, width: 30, height: 30, bgcolor: "#ef767a", opacity: .11 }}/>
          <GlassySquare sx={{ bottom: 120, left: 170, width: 22, height: 22, bgcolor: "#fff", opacity: .07 }}/>
          <Box sx={{ mb: 3, mt: { xs: 10, md: 0 }, zIndex: 2 }}>
            <SevenEdgeLogo size={54} />
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#FFF",
              letterSpacing: 1.5,
              fontSize: { xs: '1.7rem', sm: '2.1rem' },
              mb: 1.2,
              textAlign: 'center',
              zIndex: 2,
            }}
          >
            7EDGE Meal Program
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: "#00b6c3",
              fontWeight: 600,
              letterSpacing: 0.6,
              fontSize: '1.14rem',
              textAlign: 'center',
              zIndex: 2,
            }}
          >
            Quick & Easy Meal Requests
          </Typography>
        </Box>

        {/* Right Panel */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            background: {
              xs: 'linear-gradient(135deg, #15132a 0%, #21194d 50%, #322c6a 100%)',
              sm: 'linear-gradient(120deg, #322c6a 0%, #241f3e 100%)'
            },
            overflow: 'hidden',
            px: { xs: 2, sm: 0 },
            py: { xs: 3, sm: 0 },
          }}
        >
          {/* Glassy squares in background */}
          <GlassySquare sx={{ top: 80, right: 60, width: 40, height: 40, bgcolor: "#FFB300" }}/>
          <GlassySquare sx={{ bottom: 90, right: 80, width: 28, height: 28, bgcolor: "#0DB7C4" }}/>
          <GlassySquare sx={{ bottom: 160, right: 180, width: 20, height: 20, bgcolor: "#ef767a" }}/>
          <GlassySquare sx={{ top: 220, right: 100, width: 18, height: 18, bgcolor: "#fff", opacity: .07 }}/>

          <Paper
            elevation={0}
            sx={{
              borderRadius: { xs: '16px', sm: '22px' },
              p: { xs: 3, sm: 4, md: 6 },
              width: '100%',
              maxWidth: { xs: '100%', sm: 410 },
              mx: { xs: 0, sm: 'auto' },
              background: '#fff',
              boxShadow: {
                xs: '0 4px 20px 0 rgba(44,35,96,0.15), 0 1px 8px 0 rgba(44,35,96,0.1)',
                sm: '0 8px 40px 0 rgba(44,35,96,0.12), 0 2px 12px 0 rgba(44,35,96,0.08)'
              },
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 2,
            }}
          >
            {/* Mobile Logo - Only show on mobile */}
            <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 2 }}>
              <SevenEdgeLogo size={42} />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                mb: 1.5,
                color: '#22223B',
                letterSpacing: 0.2,
                fontFamily: 'inherit',
              }}
            >
              Welcome Back!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: '#50586c',
                fontWeight: 500,
                fontSize: '1.1rem',
              }}
            >
              Sign in to access your meal program
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleGoogleLogin}
              startIcon={<GoogleIcon />}
              sx={{
                py: { xs: 1.8, sm: 1.45 },
                px: 0,
                border: '1.5px solid #d4d7e6',
                color: "#23253f",
                fontWeight: 700,
                fontSize: { xs: '1rem', sm: '1.08rem' },
                background: '#fff',
                borderRadius: { xs: '10px', sm: '12px' },
                boxShadow: '0 2px 8px rgba(44,35,96,0.04)',
                letterSpacing: 0.1,
                mb: 2.5,
                minHeight: { xs: '52px', sm: '48px' }, // Better touch target
                justifyContent: 'center',
                transition: 'all 0.19s cubic-bezier(.4,0,.2,1)',
                '&:hover': {
                  borderColor: "#0DB7C4",
                  background: '#f3fdfe',
                  color: "#0DB7C4",
                  boxShadow: '0 4px 16px rgba(13,183,196,0.07)',
                },
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              CONTINUE WITH GOOGLE
            </Button>
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Login;